'use client';

import { useState } from 'react';
import {
  PLANS,
  PlanConfig,
  PlanTier,
  getYearlyTiers,
  getMonthlyTiers,
  formatPrice,
} from '@/app/config/plans';

// ============ Types ============

interface PlanSelectorProps {
  selectedTier: PlanTier | null;
  onSelect: (tier: PlanTier) => void;
}

// ============ Helpers ============

const CheckIcon = () => (
  <svg
    className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
    viewBox="0 0 12 12"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
  </svg>
);

const ComputerIcon = () => (
  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect
      x="0.75"
      y="0.75"
      width="19.5"
      height="13.5"
      rx="1.25"
      stroke="#596980"
      strokeWidth="1.5"
    />
    <rect
      x="0.75"
      y="-0.75"
      width="5.5"
      height="4.5"
      transform="matrix(1 0 0 -1 7 18.5)"
      stroke="#596980"
      strokeWidth="1.5"
    />
    <line x1="1" y1="10.5" x2="20" y2="10.5" stroke="#596980" />
  </svg>
);

// ============ Plan Card ============

interface PlanCardProps {
  plan: PlanConfig;
  isSelected: boolean;
  onSelect: () => void;
}

function PlanCard({ plan, isSelected, onSelect }: PlanCardProps) {
  const [dollars, cents] = formatPrice(plan.basePrice).replace('$', '').split('.');

  return (
    <div
      className={`relative flex flex-col h-full px-6 py-5 bg-white shadow-lg cursor-pointer transition-all ${
        isSelected ? 'ring-2 ring-blue-500' : 'hover:shadow-xl'
      } ${plan.popular ? 'border-t-4 border-t-emerald-100' : ''}`}
      onClick={onSelect}
    >
      {/* Popular badge */}
      {plan.popular && (
        <div className="absolute -top-5 mx-auto left-1/2 -translate-x-1/2">
          <div className="inline-flex text-sm font-semibold py-1.5 px-[26px] text-emerald-500 bg-emerald-100 rounded-full">
            Most Popular
          </div>
        </div>
      )}

      <div className="mb-4">
        <div className="text-lg font-semibold text-zinc-800 mb-1">{plan.displayName}</div>
        <div className="inline-flex items-baseline mb-3 leading-[58px] h-14">
          <span className="h3 font-medium text-zinc-800">$</span>
          <span className="h2 leading-7 font-merriweather text-zinc-800">
            {dollars}.
            <span className="text-[28px] leading-7 font-merriweather text-zinc-800">{cents}</span>
          </span>
          {plan.periodLabel && (
            <span className="font-medium text-slate-400 ml-2">{plan.periodLabel}</span>
          )}
        </div>
        <div className="text-slate-600 lg:min-h-[4.5rem]">{plan.description}</div>
      </div>

      <div className="px-3 py-1">
        <button
          className={`btn-lg w-full group ${
            isSelected
              ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
              : 'bg-blue-500 hover:bg-blue-700 text-white'
          }`}
        >
          {isSelected ? (
            <>
              Selected{' '}
              <span className="ml-1">
                <svg className="w-4 h-4 inline" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </>
          ) : (
            <>
              Go {plan.shortName}{' '}
              <span className="tracking-normal text-blue-300 group-hover:translate-x-0.5 transition-transform duration-150 ease-in-out ml-1">
                -&gt;
              </span>
            </>
          )}
        </button>
      </div>

      <div className="mt-4 mb-4 border-b border-slate-200"></div>

      <div className="font-medium mb-4">{plan.featuresHeading}</div>
      <ul className="text-slate-600 space-y-3 grow mb-6">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-center">
            <CheckIcon />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============ Main Component ============

export default function PlanSelector({ selectedTier, onSelect }: PlanSelectorProps) {
  const [annual, setAnnual] = useState<boolean>(true);

  // Get plan tiers based on billing period
  const tierList = annual ? getYearlyTiers() : getMonthlyTiers();
  const plans = tierList.map((tier) => PLANS[tier]);

  return (
    <div>
      {/* Pricing toggle */}
      <div className="flex justify-center items-center space-x-7 md:space-x-5 mb-9">
        <div className="text-sm text-slate-600 font-semibold text-right min-w-32">Pay Monthly</div>
        <div className="form-switch shrink-0">
          <input
            type="checkbox"
            id="plan-toggle"
            className="sr-only"
            checked={annual}
            onChange={() => setAnnual(!annual)}
          />
          <label className="bg-slate-300" htmlFor="plan-toggle">
            <span className="bg-slate-300 border-8 border-blue-500" aria-hidden="true"></span>
            <span className="sr-only text-slate-600 font-semibold">Pay yearly</span>
          </label>
        </div>
        <div className="text-sm text-slate-600 font-semibold min-w-32">
          Pay Yearly <span className="text-emerald-500">(-66%)</span>
        </div>
      </div>

      {/* Plan cards */}
      <div className="max-w-sm mx-auto grid gap-8 lg:grid-cols-3 lg:gap-6 items-start lg:max-w-none pt-4">
        {plans.map((plan) => (
          <PlanCard
            key={plan.tier}
            plan={plan}
            isSelected={selectedTier === plan.tier}
            onSelect={() => onSelect(plan.tier)}
          />
        ))}
      </div>

      {/* Support note */}
      <div className="text-center mt-8 text-slate-500 text-sm flex flex-row justify-center items-center gap-2">
        <ComputerIcon />
        All plans come with customer support
      </div>
    </div>
  );
}
